import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { songAPI } from '../services/api';

const PageContainer = styled.div`
  padding: 2rem;
  min-height: 60vh;
`;

const GroupTitle = styled.h1`
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #888;
  text-align: center;
  margin-bottom: 2rem;
`;

const SongList = styled.div`
  max-width: 700px;
  margin: 0 auto;
  display: grid;
  gap: 0.75rem;
`;

const SongRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(204, 0, 0, 0.2);
  border-radius: 6px;
  padding: 0.9rem 1rem;
`;

const SongName = styled.div`
  color: #ffffff;
  font-weight: 600;
  font-size: 1.05rem;
  flex: 1;
`;

const DownloadButton = styled.button`
  background: #0066cc;
  color: white;
  border: none;
  padding: 0.55rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #0052a3;
  }

  &:disabled {
    background: #555;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #999;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #444;
    border-top: 4px solid #cc0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function GroupSharePage() {
  const { slug } = useParams();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    loadGroup();
  }, [slug]);

  const loadGroup = async () => {
    try {
      setLoading(true);
      const data = await songAPI.getSharedGroup(slug);
      setGroup(data);
    } catch (error) {
      console.error('Error loading group:', error);
      toast.error('Group not found');
      setGroup(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (song) => {
    if (!song.has_pdf) {
      toast.error('No PDF available for this song');
      return;
    }
    try {
      setDownloadingId(song.id);
      const pdfBlob = await songAPI.getPDF(song.id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${song.name.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (!group) {
    return (
      <PageContainer>
        <EmptyState>
          <h2>Group not found</h2>
          <p>This share link is invalid or the group was deleted.</p>
        </EmptyState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <GroupTitle>{group.name}</GroupTitle>
      <Subtitle>{group.songs.length} song{group.songs.length === 1 ? '' : 's'}</Subtitle>

      {group.songs.length === 0 ? (
        <EmptyState>
          <h3>No songs in this group yet</h3>
        </EmptyState>
      ) : (
        <SongList>
          {group.songs.map((song) => (
            <SongRow key={song.id}>
              <SongName>{song.name}</SongName>
              <DownloadButton
                onClick={() => handleDownload(song)}
                disabled={!song.has_pdf || downloadingId === song.id}
              >
                {!song.has_pdf
                  ? 'No PDF'
                  : downloadingId === song.id
                    ? 'Downloading…'
                    : 'Download'}
              </DownloadButton>
            </SongRow>
          ))}
        </SongList>
      )}
    </PageContainer>
  );
}

export default GroupSharePage;
